/**
 * Task #1527 — Widget header tenant: 2 barre R2 + DB con popover dettaglio.
 * Promosso da `packages/frontend/src/components/layout/StorageQuotaWidget.tsx`.
 *
 * Click → toggle popover con: % esatte, byte usati/max, lista categorie
 * bloccate, sblocco emergenza, embedder provider attivo, link a pagina
 * dedicata. Chiusura su click outside o ESC.
 */
import { useState, useRef, useEffect } from 'react';
import { HardDrive, AlertTriangle, ShieldOff, Sparkles, ExternalLink } from 'lucide-react';
import {
  useStorageQuota,
  severityForStatus,
  formatBytes,
  type QuotaStatus,
  type StorageQuotaSnapshot,
} from '../../hooks/useStorageQuota';

const COLOR_BY_STATUS: Record<QuotaStatus, string> = {
  ok: 'bg-emerald-500',
  info: 'bg-sky-500',
  warning: 'bg-amber-500',
  critical: 'bg-orange-500',
  grace: 'bg-red-500',
  blocked: 'bg-red-700',
};

const TEXT_BY_STATUS: Record<QuotaStatus, string> = {
  ok: 'text-emerald-600',
  info: 'text-sky-600',
  warning: 'text-amber-600',
  critical: 'text-orange-600',
  grace: 'text-red-600',
  blocked: 'text-red-700',
};

const LABEL_BY_STATUS: Record<QuotaStatus, string> = {
  ok: 'OK',
  info: 'Info',
  warning: 'Attenzione',
  critical: 'Critico',
  grace: 'Tolleranza',
  blocked: 'Bloccato',
};

export interface StorageQuotaWidgetProps {
  fetchSnapshot: () => Promise<StorageQuotaSnapshot | null>;
  onNavigateDetail: () => void;
}

export function StorageQuotaWidget(props: StorageQuotaWidgetProps): JSX.Element | null {
  const { fetchSnapshot, onNavigateDetail } = props;
  const { snapshot } = useStorageQuota({ fetchSnapshot });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!snapshot) return null;

  const r2Pct = snapshot.r2.pct;
  const dbPct = snapshot.db?.pct ?? null;
  const overall: QuotaStatus = (() => {
    const list: QuotaStatus[] = [snapshot.r2.status];
    if (snapshot.db) list.push(snapshot.db.status);
    const order: QuotaStatus[] = ['blocked', 'grace', 'critical', 'warning', 'info', 'ok'];
    return order.find((s) => list.includes(s)) || 'ok';
  })();
  const sev = severityForStatus(overall);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-white/10"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Stato quota storage"
      >
        <HardDrive className={`h-4 w-4 ${TEXT_BY_STATUS[overall]}`} />
        <div className="hidden md:flex flex-col gap-0.5 w-20">
          <div className="flex items-center gap-1">
            <span className="text-[9px] text-gray-500 w-4">R2</span>
            <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded">
              <div className={`h-full rounded ${COLOR_BY_STATUS[snapshot.r2.status]}`} style={{ width: `${Math.min(100, r2Pct)}%` }} />
            </div>
          </div>
          {snapshot.db && (
            <div className="flex items-center gap-1">
              <span className="text-[9px] text-gray-500 w-4">DB</span>
              <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded">
                <div className={`h-full rounded ${COLOR_BY_STATUS[snapshot.db.status]}`} style={{ width: `${Math.min(100, dbPct ?? 0)}%` }} />
              </div>
            </div>
          )}
        </div>
        {sev === 'critical' && (
          <span className="text-[10px] font-bold text-red-600 dark:text-red-400 hidden lg:inline">!</span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-full mt-2 w-80 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl z-50 p-4 text-sm"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold text-gray-900 dark:text-gray-100">Quota storage</span>
            <span className={`text-xs font-medium ${TEXT_BY_STATUS[overall]}`}>{LABEL_BY_STATUS[overall]}</span>
          </div>

          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600 dark:text-gray-400">R2 (oggetti)</span>
                <span className="text-xs font-mono">{r2Pct.toFixed(1)}%</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded">
                <div className={`h-full rounded ${COLOR_BY_STATUS[snapshot.r2.status]}`} style={{ width: `${Math.min(100, r2Pct)}%` }} />
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {formatBytes(snapshot.r2.usedBytes)} / {formatBytes(snapshot.r2.maxBytes)}
              </div>
            </div>

            {snapshot.db ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Database</span>
                  <span className="text-xs font-mono">{(dbPct ?? 0).toFixed(1)}%</span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded">
                  <div className={`h-full rounded ${COLOR_BY_STATUS[snapshot.db.status]}`} style={{ width: `${Math.min(100, dbPct ?? 0)}%` }} />
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {formatBytes(snapshot.db.usedBytes)} / {formatBytes(snapshot.db.maxBytes)}
                </div>
              </div>
            ) : (
              <div className="text-[11px] italic text-gray-500">Database: dato non ancora disponibile</div>
            )}

            {snapshot.blockedCategories.length > 0 && (
              <div className="rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 px-2 py-1.5 flex items-start gap-2">
                <ShieldOff className="h-3.5 w-3.5 text-red-600 mt-0.5 shrink-0" />
                <div className="text-[11px] text-red-700 dark:text-red-300">
                  <div className="font-semibold">Categorie bloccate</div>
                  <div>{snapshot.blockedCategories.join(', ')}</div>
                </div>
              </div>
            )}

            {snapshot.emergencyUnblockUntil && (
              <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-2 py-1.5 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-[11px] text-amber-700 dark:text-amber-300">
                  Sblocco emergenza fino al {new Date(snapshot.emergencyUnblockUntil).toLocaleString('it-IT')}
                </div>
              </div>
            )}

            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-700">
              <Sparkles className="h-3 w-3" />
              <span>Embedder:</span>
              <span className="font-mono">{snapshot.embedderProvider ?? 'non configurato'}</span>
            </div>

            <button
              type="button"
              onClick={() => { setOpen(false); onNavigateDetail(); }}
              className="w-full mt-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
            >
              Dettaglio quota <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StorageQuotaWidget;
